import React, { useState } from 'react';

import { track, useMatchMedia } from '@betterment/js-runtime';
import { Box, Button, Card, Dialog, H2, IconButton, Separator, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { SambaAutoLink } from '@betterment/shared-components';

import { trackClick } from '../../features/track';
import { ContentCardProps } from '../data';

interface DisclosureModalProps {
  fullDisclosure: string;
  onOpenChange: () => void;
  shouldShowDisclosureModal: boolean;
}

const DisclosureModal: React.FC<DisclosureModalProps> = ({
  fullDisclosure,
  onOpenChange,
  shouldShowDisclosureModal,
}) => {
  return (
    <Dialog overlay="default" open={shouldShowDisclosureModal} onOpenChange={onOpenChange}>
      <Dialog.Content css={{ p: 'space.300' }} size="large">
        <Dialog.Title asChild={true}>
          <H2 variant="title200" css={{ mb: 'space.300' }}>
            Disclosure
          </H2>
        </Dialog.Title>
        <Dialog.Description>
          <Text variant="text100" dangerouslySetInnerHTML={{ __html: fullDisclosure }} />
        </Dialog.Description>
      </Dialog.Content>
    </Dialog>
  );
};

const Guide: React.FC<ContentCardProps> = ({
  title,
  description,
  linkText,
  imageUrl,
  dismissible,
  url,
  handleDismissal,
  handleCardClick,
  trackingModule: eventModule,
  trackingFlow: eventFlow,
  extras: { shortDisclosure, fullDisclosure, imageAltText, name: eventName, disclosureLink, linkTarget },
}) => {
  const isAtLeastSmall = useMatchMedia('atLeastSmall');
  const [shouldShowDisclosureModal, setShouldShowDisclosureModal] = useState(false);
  const featureImageSize = isAtLeastSmall ? '120px' : '72px';

  const trackedButtonClick = () => {
    const eventProperties = {
      name: `${eventName}_full_disclosure`,
      module: eventModule,
      flow: eventFlow,
    };
    track('ElementClicked', eventProperties);
    setShouldShowDisclosureModal(true);
  };

  return (
    <Card
      css={{
        position: 'relative',
        overflow: 'hidden',
        p: 'space.300',
      }}
    >
      <Box
        css={{
          display: 'flex',
          justifyContent: 'space-between',
          pr: 'space.50',
          ...(!isAtLeastSmall && { flexDirection: 'column' }),
        }}
      >
        {!isAtLeastSmall && (
          <H2 variant="title200" css={{ pr: 'space.150' }}>
            {title}
          </H2>
        )}
        <Box css={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Box css={{ display: 'flex', flexDirection: 'column', pr: 'space.200' }}>
            {isAtLeastSmall && <H2 variant="title200">{title}</H2>}
            <Text variant="text100" css={{ mb: 'space.0' }} dangerouslySetInnerHTML={{ __html: description }} />
            <Box css={{ display: 'flex', alignItems: 'center', mt: 'space.300' }}>
              {url && linkText && (
                <SambaAutoLink
                  to={url}
                  target={linkTarget}
                  rel="noopener noreferrer"
                  onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                    trackClick({ name: `${eventName}_${linkText}`, module: eventModule, flow: eventFlow });
                    handleCardClick(e);
                  }}
                  data-testid="contentCardLink"
                >
                  <Text css={{ fontWeight: 'medium' }}>{linkText}</Text>
                </SambaAutoLink>
              )}
              {dismissible && isAtLeastSmall && (
                <Button
                  css={{ marginLeft: 'space.200', cursor: 'pointer' }}
                  onClick={handleDismissal}
                  variant="ghostPrimary"
                >
                  <Text color="text.weak">Dismiss</Text>
                </Button>
              )}
            </Box>
          </Box>
          {imageUrl && (
            <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: '0 0 12.8rem' }}>
              <Box
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: featureImageSize,
                  height: featureImageSize,
                }}
              >
                {imageUrl && (
                  <img
                    className={css({ maxWidth: '100%', maxHeight: '100%' })}
                    src={imageUrl}
                    alt={imageAltText || title}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {shortDisclosure && (
        <>
          <Separator css={{ mt: 'space.300', mb: 'space.200' }} />
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Text color="text.weak" variant="text25" dangerouslySetInnerHTML={{ __html: shortDisclosure }} />
            {fullDisclosure && (
              <Button
                css={{ ml: 'space.50', _hover: { textDecoration: 'none' } }}
                variant="ghostPrimary"
                spacing="compact"
                onClick={trackedButtonClick}
              >
                <Text variant="text25">{disclosureLink}</Text>
              </Button>
            )}
          </Box>
        </>
      )}
      {dismissible && !isAtLeastSmall && (
        <Box css={{ position: 'absolute', top: 'space.200', right: 'space.200' }}>
          <IconButton name="X" aria-label="Dismiss card" label="Dismiss card" onClick={handleDismissal} />
        </Box>
      )}
      {shouldShowDisclosureModal && fullDisclosure && (
        <DisclosureModal
          fullDisclosure={fullDisclosure}
          onOpenChange={() => setShouldShowDisclosureModal(false)}
          shouldShowDisclosureModal={shouldShowDisclosureModal}
        />
      )}
    </Card>
  );
};

export default Guide;
